import { FC, useEffect, useMemo } from 'react';
import { css } from '@emotion/react';
import { useInView } from 'react-intersection-observer';
import { CallIntelConstant, CallIntelligenceV2, CallIntelTypes, CallIntelUtils } from '@frontend/api-call-intel';
import { Trans, useTranslation } from '@frontend/i18n';
import { sentry } from '@frontend/tracking';
import { theme } from '@frontend/theme';
import { Heading, Text } from '@frontend/design-system';
import { OpportunitiesByTypeChart } from '../../charts/opportunities-by-type-chart';
import { useActiveFilters } from '../../hooks/use-active-filters';
import { useCallIntelShallowStore } from '../../hooks/use-call-intel-store';
import { useTotalCallsAnalyzedCount } from '../../hooks/use-total-calls-analyzed-count';

export const CallTrends: FC = () => {
  const { t } = useTranslation('call-intelligence');

  const { setIsLoadingAggregatedData } = useCallIntelShallowStore('setIsLoadingAggregatedData');
  const { filters } = useActiveFilters();

  const { totalCallsAnalyzed } = useTotalCallsAnalyzedCount();

  const { inView: isVisible, ref } = useInView({ triggerOnce: true });

  const overviewRequestPayload = useMemo(() => {
    return {
      ...CallIntelUtils.generateChartsPayload({ filters }),
      includesAnalysis: [
        CallIntelTypes.IncludesAnalysis.CALL_APPOINTMENT_TYPE,
        CallIntelTypes.IncludesAnalysis.CALL_CATEGORY,
      ],
      includesHydration: [],
    };
  }, [filters]);

  const { data: overviewData, isLoading: isOverviewLoading } = CallIntelligenceV2.Queries.useGetCallsOverviewQuery({
    request: overviewRequestPayload,
    options: {
      enabled: !!filters.locations?.length && isVisible,
      retry: false,
      cacheTime: CallIntelConstant.DEFAULT_CACHE_TIME,
      staleTime: CallIntelConstant.DEFAULT_CACHE_TIME,
      onError: (error) => {
        console.error('Error fetching calls overview data for call trends:', error);
        sentry.warn({
          team: 'analytics',
          topic: 'call-intelligence',
          error: `Error fetching calls overview data for call trends: ${error}`,
          addContext: {
            name: 'filters',
            context: { payload: overviewRequestPayload },
          },
        });
      },
    },
  });

  useEffect(() => {
    setIsLoadingAggregatedData?.(isOverviewLoading);
  }, [isOverviewLoading, setIsLoadingAggregatedData]);

  return (
    <section css={defaultStyles.container} ref={ref} data-testid='call-trends-section'>
      <div css={defaultStyles.headerContainer}>
        <Heading level={3}>{t('Call Trends')}</Heading>
        <Text size='medium' color='subdued'>
          <Trans t={t} count={totalCallsAnalyzed} values={{ totalCallsAnalyzed }}>
            <strong>{{ totalCallsAnalyzed }}</strong> calls analyzed
          </Trans>
        </Text>
      </div>
      <div css={defaultStyles.cardContainer}>
        <OpportunitiesByTypeChart
          focusLabel={t('Calls')}
          infoTipId='callsByCategory'
          isLoading={isOverviewLoading}
          subViewType='category'
          title={t('Calls by Category')}
          types={overviewData?.categories ?? {}}
        />
        <OpportunitiesByTypeChart
          focusLabel={t('Calls')}
          infoTipId='callsByAppointmentType'
          isLoading={isOverviewLoading}
          subViewType='appointment-type'
          title={t('Calls by Appointment Type')}
          types={overviewData?.appointmentTypes ?? {}}
        />
      </div>
    </section>
  );
};

const defaultStyles = {
  container: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
  `,
  headerContainer: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
  `,
  cardContainer: css`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: ${theme.spacing(3)};
  `,
};
